import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../services/api";
import styles from "./Home.module.css";
import Card from "../../components/common/Card/Card";
import Button from "../../components/common/Button/Button";
import { fetchCountsService } from "../../services/fetchCountsService";
import { useTradeRecommendations } from "../../hooks/useTradeRecommendations";
import { useRealtimePrices } from "../../hooks/useRealtimePrices";
import { subscribeSymbols, unsubscribeDelayed } from "../../ws/marketSubscriptions";
import { usePermission } from "../../hooks/usePermissions";
import "../StockDetails/StockDetailsPage.css";

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const Home = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const [count, setCount] = useState(null);
  const { tradeRecommendations, fetchTradeRecommendations } = useTradeRecommendations();
  const { prices: realtimePrices } = useRealtimePrices();

  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const suggestionsRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchInput = useCallback((value) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (value.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/trading/search?query=${encodeURIComponent(value.trim())}`);
        const data = res?.data || [];
        setSuggestions(Array.isArray(data) ? data.slice(0, 10) : []);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      }
    }, 300);
  }, []);

  const handleDirectSearch = useCallback(async () => {
    const trimmed = searchQuery.trim();
    if (!trimmed) return;

    setShowSuggestions(false);
    setIsSearching(true);

    try {
      const res = await api.get(`/trading/search?query=${encodeURIComponent(trimmed)}`);
      const data = res?.data || [];
      if (Array.isArray(data) && data.length > 0) {
        navigate('/stock-details', { state: { selectedStock: data[0] } });
      } else {
        alert(`No stock found matching "${trimmed}".`);
      }
    } catch (err) {
      alert(err?.message || "Search failed. Please try again.");
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery, navigate]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleDirectSearch();
  };

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const countResponse = await fetchCountsService.getCounts();
        setCount(countResponse);
      } catch (error) {
        console.error("Error fetching counts:", error);
        throw error;
      }
    };

    fetchCounts();
    fetchTradeRecommendations(1, 10000);
  }, [fetchTradeRecommendations]);

  const scriptsToSubscribe = useMemo(() => {
    return [...new Set(
      tradeRecommendations
        .map(trade => trade.script || trade.script_id)
        .filter(Boolean)
        .map(s => s.toUpperCase().trim())
    )];
  }, [tradeRecommendations]);

  useEffect(() => {
    if (scriptsToSubscribe.length === 0) return;
    const page = "Home";
    const context = "TradeStats";
    subscribeSymbols(scriptsToSubscribe, page, context);
    return () => {
      unsubscribeDelayed(scriptsToSubscribe, page, context);
    };
  }, [scriptsToSubscribe]);

  const tradeStats = useMemo(() => {
    let totalRecommendations = 0;
    let liveCalls = 0;
    let stoplossHit = 0;
    let targetHit = 0;
    let liveProfit = 0;
    let potentialProfit = 0;
    let targetHitProfit = 0;
    let stoplossHitLoss = 0;

    tradeRecommendations.forEach(trade => {
      totalRecommendations++;

      const tradeStatus = trade.status || trade.recommendationstatus;
      const isLive = String(tradeStatus).toLowerCase() === "live";

      if (isLive) liveCalls++;
      if (String(tradeStatus).toLowerCase() === "stoploss hit") stoplossHit++;
      if (String(tradeStatus).toLowerCase() === "target hit") targetHit++;

      const tradeRecommendationId = Number(trade.tradeRecommendationId || trade.traderecommendationid);
      const recoPriceLow = Number(trade.recoPriceLow || trade.recopricelow || 0);
      const entryPrice = Number(trade.entryPrice || trade.entryprice || 0);
      const basePrice = recoPriceLow > 0 ? recoPriceLow : entryPrice;

      const targetOne = Number(trade.targetOne || trade.targetone || 0);
      const target = Number(trade.targetPrice || trade.targetprice || 0);
      const targetPrice = targetOne > 0 ? targetOne : target;

      if (tradeRecommendationId === 1 && targetPrice > 0 && basePrice > 0) {
        potentialProfit += (targetPrice - basePrice);
      } else if (tradeRecommendationId === 2 && targetPrice > 0 && basePrice > 0) {
        potentialProfit += (basePrice - targetPrice);
      }

      const symbolKey = (trade.script || trade.script_id || trade.stocksymbol || trade.stockSymbol || "").toUpperCase().trim();
      const rt = realtimePrices?.[trade.token] || realtimePrices?.[String(trade.token)] || realtimePrices?.[symbolKey];
      let ltp = isLive ? (rt?.price ?? Number(trade.value || trade.ltp || basePrice)) : Number(trade.value || trade.ltp || basePrice);
      const exitPriceLow = Number(trade.exitPriceLow || trade.exitpricelow || 0);

      let actual_profit = 0;
      if (isLive) {
        if (tradeRecommendationId === 1) {
          actual_profit = ltp - basePrice;
        } else if (tradeRecommendationId === 2) {
          actual_profit = basePrice - ltp;
        }
        liveProfit += (actual_profit || 0);
      } else {
        // Calculate closed profit/loss
        let closedProfit = 0;
        if (tradeRecommendationId === 1 && exitPriceLow > 0 && basePrice > 0) {
          closedProfit = exitPriceLow - basePrice;
        } else if (tradeRecommendationId === 2 && exitPriceLow > 0 && basePrice > 0) {
          closedProfit = basePrice - exitPriceLow;
        }

        if (String(tradeStatus).toLowerCase() === "target hit") {
          targetHitProfit += closedProfit;
        } else if (String(tradeStatus).toLowerCase() === "stoploss hit") {
          stoplossHitLoss += closedProfit;
        }
      }
    });

    return { totalRecommendations, liveCalls, stoplossHit, targetHit, liveProfit, potentialProfit, targetHitProfit, stoplossHitLoss };
  }, [tradeRecommendations, realtimePrices]);
  const stats = [
    {
      label: "Total Users",
      value: count?.totalUsers,
      icon: <span className="material-symbols-outlined">groups</span>,
      link: "/users"
    },
    {
      label: "Active Analysts",
      value: count?.totalAnalysts,
      icon: <span className="material-symbols-outlined">monitoring</span>,
      link: "/users"
    },
    {
      label: "News",
      value: count?.totalPosts,
      icon: <span className="material-symbols-outlined">newspaper</span>,
      link: "/newsfeed"
    },
    { label: "Total Tickets", value: count?.totalGrievance, icon: <span className="material-symbols-outlined">report</span>, link: "/tickets" },
  ];

  const visibleStats = useMemo(() => {
    return stats.filter(stat => {
      if (!stat.permission) return true;
      if (Array.isArray(stat.permission)) {
        return stat.permission.some(p => hasPermission(p));
      }
      return hasPermission(stat.permission);
    });
  }, [stats, hasPermission]);

  // const quickActions = [
  //   {
  //     title: "Manage Users",
  //     description: "View, edit, and manage all registered users",
  //     link: "/users",
  //     icon: "👥",
  //     color: "blue",
  //   },
  //   {
  //     title: "View Analytics",
  //     description: "Monitor platform performance and user engagement",
  //     link: "/dashboard",
  //     icon: "📊",
  //     color: "green",
  //   },
  //   {
  //     title: "Market Data",
  //     description: "Access real-time stock market information",
  //     link: "/stocks",
  //     icon: "📈",
  //     color: "orange",
  //   },
  //   {
  //     title: "News Feed",
  //     description: "Latest financial news and market updates",
  //     link: "/news",
  //     icon: "📰",
  //     color: "purple",
  //   },
  // ];

  return (
    <div className={styles.homePage}>
      <div className={styles.statsSection}>
        {hasPermission("VIEW_STOCK_DETAILS") && (
          <div className={styles.searchContainer}>
            <div className={`sd-search-section ${styles.searchSection}`} ref={suggestionsRef}>
              <div className={`sd-search-wrap ${styles.searchWrap}`}>
                <SearchIcon />
                <input
                  type="text"
                  placeholder="Search Stock..."
                  value={searchQuery}
                  onChange={(e) => handleSearchInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  className={styles.searchInput}
                />
              </div>
              <button type="button" className={`sd-btn-search ${styles.searchBtn}`} onClick={handleDirectSearch} disabled={isSearching || !searchQuery.trim()}>
                {isSearching ? "..." : "Search"}
              </button>
              {showSuggestions && suggestions.length > 0 && (
                <div className={`sd-suggestions ${styles.searchSuggestions}`}>
                  {suggestions.map((s, idx) => (
                    <div key={`${s.symbol}-${idx}`} className="sd-suggestion-item" onClick={() => navigate('/stock-details', { state: { selectedStock: s } })}>
                      <div className="sd-suggestion-icon">{(s.symbol || "?").slice(0, 3)}</div>
                      <div className="sd-suggestion-info">
                        <div className="sd-suggestion-symbol">{s.symbol}</div>
                        <div className="sd-suggestion-name">{s.name || "—"}</div>
                      </div>
                      <span className="sd-suggestion-exchange">{s.exchange || "NSE"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {visibleStats.length > 0 && (
          <>
            <h2 className={`${styles.sectionTitle} ${styles.centeredTitle}`}>Platform Overview</h2>
            <div className={styles.statsGrid}>
              {visibleStats.map((stat, index) => (
                <Link to={stat.link} key={index} className={styles.statLink}>
                  <Card className={styles.statCard} hover clickable>
                    <div className={styles.statIcon}>{stat.icon}</div>
                    <div className={styles.statContent}>
                      <div className={styles.statValue}>{stat.value}</div>
                      <div className={styles.statLabel}>{stat.label}</div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </>
        )}

      </div>

      <div className={styles.statsSection}>
        <h2 className={styles.sectionTitle}>Trade Recommendation Overview</h2>
        <div className={styles.statsGrid}>
          {/* Card 1: Recommendations */}
          <Link to="/traderecommendations" className={styles.statLink}>
            <Card className={`${styles.statCard} ${styles.tradeStatCard}`} hover clickable>
              <div className={styles.tradeStatContent}>
                <div className={styles.tradeStatHeader}>Recommendations</div>
                <div className={styles.tradeStatSplit}>
                  <div className={styles.tradeStatColumn}>
                    <div className={styles.tradeStatValue}>{tradeStats.totalRecommendations}</div>
                    <div className={styles.tradeStatSublabel}>Total</div>
                  </div>
                  <div className={styles.tradeStatDivider}></div>
                  <div className={styles.tradeStatColumn}>
                    <div className={`${styles.tradeStatValue} ${styles.textGreen}`}>{tradeStats.liveCalls}</div>
                    <div className={styles.tradeStatSublabel}>Live Calls</div>
                  </div>
                </div>
              </div>
            </Card>
          </Link>

          {/* Card 2: Closed Calls */}
          <Link to="/traderecommendations" className={styles.statLink}>
            <Card className={`${styles.statCard} ${styles.tradeStatCard}`} hover clickable>
              <div className={styles.tradeStatContent}>
                <div className={styles.tradeStatHeader}>Closed Calls</div>
                <div className={styles.tradeStatSplit}>
                  <div className={styles.tradeStatColumn}>
                    <div className={`${styles.tradeStatValue} ${styles.textGreen}`}>{tradeStats.targetHit}</div>
                    <div className={styles.tradeStatSublabel}>Target Hit</div>
                  </div>
                  <div className={styles.tradeStatDivider}></div>
                  <div className={styles.tradeStatColumn}>
                    <div className={`${styles.tradeStatValue} ${styles.textRed}`}>{tradeStats.stoplossHit}</div>
                    <div className={styles.tradeStatSublabel}>Stoploss Hit</div>
                  </div>
                </div>
              </div>
            </Card>
          </Link>

          {/* Card 3: Live & Potential Profit */}
          <Link to="/traderecommendations" className={styles.statLink}>
            <Card className={`${styles.statCard} ${styles.tradeStatCard}`} hover clickable>
              <div className={styles.tradeStatContent}>
                <div className={styles.tradeStatHeader}>Expected Profits</div>
                <div className={styles.tradeStatSplit}>
                  <div className={styles.tradeStatColumn}>
                    <div className={`${styles.tradeStatValue} ${tradeStats.liveProfit < 0 ? styles.textRed : styles.textGreen}`}>
                      ₹{Math.abs(tradeStats.liveProfit).toFixed(2)}
                    </div>
                    <div className={styles.tradeStatSublabel}>Live Profit</div>
                  </div>
                  <div className={styles.tradeStatDivider}></div>
                  <div className={styles.tradeStatColumn}>
                    <div className={`${styles.tradeStatValue} ${styles.textGreen}`}>
                      ₹{tradeStats.potentialProfit.toFixed(2)}
                    </div>
                    <div className={styles.tradeStatSublabel}>Potential Profit</div>
                  </div>
                </div>
              </div>
            </Card>
          </Link>

          {/* Card 4: Target Hit & Stoploss Hit P&L */}
          <Link to="/traderecommendations" className={styles.statLink}>
            <Card className={`${styles.statCard} ${styles.tradeStatCard}`} hover clickable>
              <div className={styles.tradeStatContent}>
                <div className={styles.tradeStatHeader}>Closed P&amp;L</div>
                <div className={styles.tradeStatSplit}>
                  <div className={styles.tradeStatColumn}>
                    <div className={`${styles.tradeStatValue} ${styles.textGreen}`}>
                      ₹{tradeStats.targetHitProfit.toFixed(2)}
                    </div>
                    <div className={styles.tradeStatSublabel}>Target Hit Profit</div>
                  </div>
                  <div className={styles.tradeStatDivider}></div>
                  <div className={styles.tradeStatColumn}>
                    <div className={`${styles.tradeStatValue} ${styles.textRed}`}>
                      ₹{Math.abs(tradeStats.stoplossHitLoss).toFixed(2)}
                    </div>
                    <div className={styles.tradeStatSublabel}>Stoploss Hit Loss</div>
                  </div>
                </div>
              </div>
            </Card>
          </Link>
        </div>
      </div>

      {/* <div className={styles.quickActionsSection}>
        <h2 className={styles.sectionTitle}>Quick Actions</h2>
        <div className={styles.actionsGrid}>
          {quickActions.map((action, index) => (
            <Link key={index} to={action.link} className={styles.actionLink}>
              <Card
                className={`${styles.actionCard} ${styles[action.color]}`}
                hover
                clickable
              >
                <div className={styles.actionIcon}>{action.icon}</div>
                <div className={styles.actionContent}>
                  <h3 className={styles.actionTitle}>{action.title}</h3>
                  <p className={styles.actionDescription}>
                    {action.description}
                  </p>
                </div>
                <div className={styles.actionArrow}>→</div>
              </Card>
            </Link>
          ))}
        </div>
      </div> */}
    </div>
  );
};

export default Home;
